import { useState, useEffect, useRef } from 'react'
import { motion, useAnimation } from 'framer-motion'
import { Icon } from '@/icons'
import clsx from 'clsx';
import WhiteDotLine from '@/assets/img/white_dot_line.png';
import PrimaryDotLine from '@/assets/img/primary_dot_line.png';

type StudentFlashCardProps = {
  className?: string;
  initialStep?: 1 | 2 | 3;
  onRecordingComplete?: (audioBlob: Blob, audioUrl: string) => void;
  existingAudioUrl?: string | null; // 이미 녹음된 URL (S3 URL)
};

export default function StudentFlashCard({
  className = '',
  initialStep = 1,
  onRecordingComplete,
  existingAudioUrl = null,
}: StudentFlashCardProps) {
  // 기존 녹음이 있으면 step 3(재생 모드)으로 시작, 없으면 step 1
  const [step, setStep] = useState(existingAudioUrl ? 3 : initialStep)
  const [playing, setPlaying] = useState(false)
  const maxTime = 20
  // 기존 녹음이 있을 때는 임시로 5초로 설정 (실제 duration은 audio onLoadedMetadata에서 얻음)
  const [recordedDuration, setRecordedDuration] = useState(existingAudioUrl ? 5 : 0)
  const [timeElapsed, setTimeElapsed] = useState(0)
  const [currentTime, setCurrentTime] = useState(0) // 재생 중 현재 시간
  
  // 녹음 관련 ref
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(existingAudioUrl) // 기존 URL로 초기화
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  
  // existingAudioUrl prop이 변경되면 audioUrl 상태 업데이트
  useEffect(() => {
    if (existingAudioUrl) {
      console.log('RecordingBox: existingAudioUrl 업데이트:', existingAudioUrl);
      setAudioUrl(existingAudioUrl);
      setStep(3); // 재생 모드로 변경
    }
  }, [existingAudioUrl]);

  // 오디오 URL이 있을 때 실제 duration 가져오기 (blob URL 포함)
  useEffect(() => {
    if (audioUrl && audioRef.current) {
      const handleLoadedMetadata = () => {
        if (audioRef.current && !isNaN(audioRef.current.duration) && isFinite(audioRef.current.duration)) {
          const duration = Math.floor(audioRef.current.duration)
          console.log('오디오 duration 로드됨:', duration, 'URL:', audioUrl);
          if (duration > 0) {
            setRecordedDuration(duration)
          }
        }
      }
      
      const handleError = (e: Event) => {
        console.error('오디오 로드 에러:', e);
        console.error('오디오 URL:', audioUrl);
      }
      
      // 이미 로드된 경우 즉시 duration 가져오기
      if (audioRef.current.readyState >= 1 && audioRef.current.duration > 0) {
        handleLoadedMetadata()
      } else {
        audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata)
        audioRef.current.addEventListener('error', handleError)
        // 강제로 로드 시도
        audioRef.current.load()
      }
      
      return () => {
        audioRef.current?.removeEventListener('loadedmetadata', handleLoadedMetadata)
        audioRef.current?.removeEventListener('error', handleError)
      }
    }
  }, [audioUrl])

  // 녹음 시작
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
        const url = URL.createObjectURL(audioBlob)
        setAudioUrl(url)
        setAudioBlob(audioBlob)
        
        // timeElapsed를 recordedDuration에 저장
        const finalDuration = timeElapsed
        console.log('녹음 완료, 저장된 시간:', finalDuration)
        setRecordedDuration(finalDuration)
        
        // 오디오 메타데이터에서 실제 duration 가져오기 (추가 확인)
        const tempAudio = new Audio(url)
        tempAudio.addEventListener('loadedmetadata', () => {
          const actualDuration = Math.floor(tempAudio.duration)
          console.log('오디오 실제 duration:', actualDuration)
          if (actualDuration > 0) {
            setRecordedDuration(actualDuration)
          }
        })
        tempAudio.load()
        
        // 부모 컴포넌트에 녹음 완료 알림
        if (onRecordingComplete) {
          onRecordingComplete(audioBlob, url)
        }
        
        // 스트림 정리
        stream.getTracks().forEach(track => track.stop())
      }

      mediaRecorder.start()
      setStep(2)
      setTimeElapsed(0)
    } catch (error) {
      console.error('녹음 시작 실패:', error)
      alert('마이크 접근 권한이 필요합니다.')
    }
  }

  // 녹음 중지
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop()
      setStep(3)
    }
  }

  // 녹음 재시작
  const resetRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl)
    }
    setAudioUrl(null)
    setAudioBlob(null)
    setTimeElapsed(0)
    setRecordedDuration(0)
    setCurrentTime(0)
    setPlaying(false)
    setStep(1)
  }

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (step === 2) {
      timer = setInterval(() => {
        setTimeElapsed(prev => {
          if (prev >= maxTime) {
            stopRecording()
            return maxTime
          }
          return prev + 1
        })
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [step])

  // 애니메이션 컨트롤러
  const controls = useAnimation()

  // Play 버튼 핸들러
  const handlePlayPause = () => {
    if (!audioRef.current || !audioUrl) return

    if (!playing) {
      // 재생 시작 시 현재 시간을 0으로 리셋
      setCurrentTime(0)
      audioRef.current.currentTime = 0
      audioRef.current.play()
      setPlaying(true)
    } else {
      audioRef.current.pause()
      setPlaying(false)
    }
  }

  // 오디오 재생 완료 및 시간 업데이트 핸들러
  useEffect(() => {
    if (audioRef.current) {
      const handleTimeUpdate = () => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime)
        }
      }
      
      const handleEnded = () => {
        setPlaying(false)
        setCurrentTime(0)
      }
      
      const handlePause = () => {
        setPlaying(false)
        // 일시정지 시 현재 시간 유지
      }
      
      const handlePlay = () => {
        setPlaying(true)
        // 재생 시작 시 현재 시간을 0으로 리셋하지 않고 유지 (사용자가 원하는 위치에서 재생)
      }
      
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate)
      audioRef.current.addEventListener('ended', handleEnded)
      audioRef.current.addEventListener('pause', handlePause)
      
      return () => {
        audioRef.current?.removeEventListener('timeupdate', handleTimeUpdate)
        audioRef.current?.removeEventListener('ended', handleEnded)
        audioRef.current?.removeEventListener('pause', handlePause)
      }
    }
  }, [audioUrl])
  
  // 컴포넌트 언마운트 시 cleanup
  useEffect(() => {
    return () => {
      // 기존 S3 URL이 아닌 로컬 Blob URL만 해제
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl)
      }
      // 녹음 중이면 중지
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
    }
  }, [])


  const minutes = String(Math.floor(timeElapsed / 60)).padStart(2, '0')
  const seconds = String(timeElapsed % 60).padStart(2, '0')
  const timeDisplay = `${minutes}:${seconds}`
  
  const recordedMinutes = String(Math.floor(recordedDuration / 60)).padStart(2, '0')
  const recordedSeconds = String(recordedDuration % 60).padStart(2, '0')
  const recordedTimeDisplay = `${recordedMinutes}:${recordedSeconds}`
  
  return (
    <div className={clsx('flex flex-col gap-3 items-center p-4 my-3 w-full bg-gray-50 rounded-2xl', className )}>
      {/* 숨겨진 오디오 엘리먼트 - 항상 렌더링 */}
      <audio 
        ref={audioRef} 
        src={audioUrl || undefined} 
        style={{ display: 'none' }}
        preload="metadata"
      />
      
      {/* 1번 섹션: 녹음 시작 버튼 */}
      {step === 1 && (
        <section className='flex gap-2 items-center cursor-pointer' onClick={startRecording}>
          <button
            className='flex justify-center items-center bg-white rounded-full border border-gray-200 size-10'>
            <Icon name='mic' />
          </button>
          <p className='text-lg font-bold'>단어를 녹음해주세요.</p>
        </section>
      )}

      {/* 2번 섹션: 녹음 중에만 보임 */}
      {step === 2 && (
        <section className='flex gap-3 justify-center items-center w-full'>
          <button
            onClick={stopRecording}
            className='flex justify-center items-center bg-white rounded-full border border-gray-200 size-10'>
            <Icon name='stop' />
          </button>

          <div className='h-9 back_gra max-w-[200px] w-full flex items-center overflow-hidden'>
            <div className='w-full h-[2px] mr-4 ml-2 overflow-hidden'>
                <motion.div className='overflow-hidden relative mr-4 ml-2 h-[2px]'
                initial={{ width: '0%' }}
                animate={{
                width: `${(timeElapsed / maxTime) * 100}%`,
                }}
                transition={{ duration: 1, ease: 'linear' }}
                >
                <div className='absolute top-0 left-0 w-[200px] h-px border border-indigo-500 border-dashed'/>
                </motion.div>
            </div>
            <span className='pr-2 text-xs text-white'>{timeDisplay}</span>
          </div>
        </section>
      )}

      {/* 3번 섹션: 재생 & 새로고침 */}
      {step === 3 && (
        <section className='flex gap-3 justify-center items-center w-full'>
          <button
            className='flex justify-center items-center bg-white rounded-full border border-gray-200 size-10'
            onClick={resetRecording}
          >
            <Icon name='refresh' />
          </button>
          <div className='w-full h-9 back_gra max-w-[200px] flex items-center'>
          <button
            className='pl-2'
            onClick={handlePlayPause}
          >
            <Icon name={playing ? 'pause' : 'play'} size={16} />
          </button>
            <div className='relative w-full h-[2px] overflow-hidden mx-2'>
              {/* 흰색 선: 항상 전체 길이로 표시 */}
              <div className='absolute top-0 left-0 w-full h-px border border-white border-dashed'></div>
              {/* 보라색 선: 재생 중일 때만 0부터 현재 재생 시간까지 진행, 아닐 때는 0% */}
              <motion.div 
                key={`progress-${recordedDuration}-${playing ? 'playing' : 'static'}`}
                className='overflow-hidden relative  h-[2px]'
                initial={playing ? { width: '0%' } : false}
                animate={{
                  width: playing && recordedDuration > 0
                    ? `${(currentTime / recordedDuration) * 100}%` 
                    : '0%'
                }}
                transition={playing 
                  ? { duration: 0 } // 재생 중일 때는 지연 없이 실시간 업데이트
                  : { 
                      duration: 0.3, // 정지 상태일 때는 빠르게 0%로
                      ease: 'easeOut' 
                    }
                }
              >
                <div className='absolute top-0 left-0 w-[200px] h-px border border-indigo-500 border-dashed'/>
              </motion.div>
            </div>
            <span className='pr-2 text-xs text-white'>
              {recordedDuration > 0 ? recordedTimeDisplay : '00:00'}
            </span>
          </div>
        </section>
      )}
    </div>
  )
}